import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Users, Share2, Calendar, Award, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompletionPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joinedCommunity, setJoinedCommunity] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCommunity = async () => {
    try {
      await axios.post(`${API}/community/join`, {}, { withCredentials: true });
      setJoinedCommunity(true);
      toast.success("You've joined the Builder Community waitlist!");
    } catch (error) {
      toast.error("Failed to join community");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="w-24 h-24 rounded-full bg-[#d4af37]/20 flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-12 h-12 text-[#d4af37]" />
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-6">
            You Did It.
          </h1>
          
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            You went from prayer to prototype in 7 days. Most people never get here. 
            You're not most people.
          </p>
          
          <p className="text-zinc-500 mt-6 max-w-xl mx-auto">
            Whatever your decision, GO, PIVOT, or RELEASE, you moved with faith and courage. 
            That matters.
          </p>
        </div>

        {/* Founding Member Badge */}
        {user?.is_founding_member && (
          <Card className="bg-[#d4af37]/10 border-[#d4af37]/30 p-6 mb-8 text-center">
            <Award className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
            <h3 className="text-white font-medium mb-2">Founding Member</h3>
            <p className="text-zinc-400 text-sm">
              You're one of the first 50 to complete Prayer to Prototype
            </p>
          </Card>
        )}

        {/* Next Steps */}
        <h2 className="text-2xl font-light text-white text-center mb-8">Here's What's Next</h2>
        
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {/* Take Action */}
          <Card className="bg-[#1f1f1f] border-zinc-800 p-6" data-testid="action-card">
            <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-[#d4af37]" />
            </div>
            <h3 className="text-white font-medium mb-2">Take Action</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Your 14 day plan from Day 7 starts now. Block time on your calendar. 
              Don't let momentum fade.
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/day/7')}
              className="border-zinc-700 text-zinc-300 hover:border-[#d4af37]/50 w-full"
            >
              Review Day 7
            </Button>
          </Card>

          {/* Join Community */}
          <Card className="bg-[#1f1f1f] border-[#d4af37]/30 p-6" data-testid="community-card">
            <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-[#d4af37]" />
            </div>
            <h3 className="text-white font-medium mb-2">Join the Builder Community</h3>
            <p className="text-zinc-400 text-sm mb-4">
              You've completed the sprint. You're eligible for early access to the Builder Community.
            </p>
            <Button 
              onClick={handleJoinCommunity}
              disabled={joinedCommunity}
              className={joinedCommunity 
                ? "bg-zinc-700 text-zinc-400 w-full" 
                : "btn-gold w-full"}
              data-testid="join-community-btn"
            >
              {joinedCommunity ? (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Joined Waitlist
                </span>
              ) : (
                "Join the Waitlist"
              )}
            </Button>
          </Card>

          {/* Share Story */}
          <Card className="bg-[#1f1f1f] border-zinc-800 p-6" data-testid="share-card">
            <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center mb-4">
              <Share2 className="w-6 h-6 text-[#d4af37]" />
            </div>
            <h3 className="text-white font-medium mb-2">Share Your Story</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Your journey could inspire someone else to start. Share what you built.
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                const text = "I just completed Prayer to Prototype - a 7-day sprint from idea to live landing page! 🙏✨";
                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
              }}
              className="border-zinc-700 text-zinc-300 hover:border-[#d4af37]/50 w-full"
            >
              Share on Twitter
            </Button>
          </Card>
        </div>

        {/* Final Blessing */}
        <Card className="bg-[#151515] border-[#d4af37]/20 p-10 text-center" data-testid="blessing-card">
          <p className="text-zinc-300 text-lg italic leading-relaxed mb-6">
            "The LORD bless you and keep you. May He give you courage to build 
            and wisdom to steward what He's given you."
          </p>
          <p className="text-[#d4af37] font-medium">Go build something beautiful.</p>
        </Card>

        {/* Back to Dashboard */}
        <div className="text-center mt-12">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-zinc-500 hover:text-white"
          >
            Back to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CompletionPage;
